import classNames from 'classnames';
import type { CSSProperties } from 'react';

import * as sliderStyles from './video-helper.module.scss';
import * as videoPlayerStyles from './video-player.module.scss';

export const createVideoAndImageSliderStyle = (
  isPortraitOrientation: boolean,
  isFullWidthLayout: boolean,
  isMeridian: boolean,
  isPopup?: boolean
) => ({
  mainContainer: sliderStyles.mainContainer,
  videoContainer: classNames(
    sliderStyles.videoContainer,
    isPortraitOrientation && sliderStyles.portrait,
    isFullWidthLayout && sliderStyles.fullWidth,
    isMeridian && sliderStyles.meridian,
    isPopup && sliderStyles.popup
  ),
  videoBackground: classNames(
    sliderStyles.videoBackground,
    isFullWidthLayout && sliderStyles.fullWidth,
    isMeridian && sliderStyles.meridian
  ),
  videoOverlay: classNames(
    sliderStyles.videoOverlay,
    isFullWidthLayout && sliderStyles.fullWidth,
    isMeridian && sliderStyles.meridian
  ),
  videoIframe: classNames(sliderStyles.videoIframe, isFullWidthLayout && sliderStyles.fullWidth),
  imageStyle: classNames(
    sliderStyles.imageStyle,
    isPortraitOrientation && sliderStyles.portrait,
    isFullWidthLayout && sliderStyles.fullWidth,
    isMeridian && sliderStyles.meridian
  ),
  imageTitle: classNames(
    sliderStyles.imageTitle,
    isFullWidthLayout && sliderStyles.fullWidth,
    isMeridian && sliderStyles.meridian
  ),
});

export const createVideoPlayerStyling = (isTitleLarge: boolean) => ({
  wrapperClassName: videoPlayerStyles.wrapper,
  controlsWrapperClassName: videoPlayerStyles.controlsWrapper,
  playControlClassName: videoPlayerStyles.playControl,
  preTitleCardClassName: videoPlayerStyles.preTitleCard,
  preTitleCardAnimateClassName: videoPlayerStyles.animate,
  contentWrapperClassName: videoPlayerStyles.contentWrapper,
  titlePlaceHolderClassName: classNames(
    videoPlayerStyles.titlePlaceHolder,
    isTitleLarge && videoPlayerStyles.titleLarge
  ),
  titleAnimateClassName: videoPlayerStyles.titleAnimate,
  mainTitleClassName: classNames(
    videoPlayerStyles.mainTitle,
    isTitleLarge && videoPlayerStyles.titleLarge
  ),
  arrowWrapperClassName: videoPlayerStyles.arrowWrapper,
  eyebrowPreTitleWrapperClassName: videoPlayerStyles.eyebrowPreTitleWrapper,
  eyebrowPreTitleTextClassName: videoPlayerStyles.eyebrowPreTitleText,
  eyebrowPreTitleIconClassName: videoPlayerStyles.eyebrowPreTitleIcon,
  subtitleClassName: videoPlayerStyles.subtitle,
});

export type ContainerSize = { width: number; height: number };

export const calculateScaleForDifferentAspectRatio = (
  containerElement: HTMLElement | null,
  containerSize: ContainerSize
) => {
  const videoElement = containerElement?.getElementsByTagName('video')[0];
  if (!videoElement?.videoWidth || !videoElement.videoHeight) {
    return null;
  }

  const videoAspectRatio = videoElement.videoWidth / videoElement.videoHeight;

  if (videoAspectRatio >= 16 / 9) {
    return null;
  }

  const actualVideoWidth = videoElement.offsetHeight * videoAspectRatio;

  return actualVideoWidth < containerSize.width
    ? `${containerSize.width / actualVideoWidth}`
    : null;
};

export const createVideoWrapperStyles = (
  containerSize: ContainerSize | null,
  scale: string | undefined
): CSSProperties => {
  if (!containerSize) {
    return {};
  }

  const heightForWidth = (containerSize.width * 9) / 16;
  const widthForHeight = (containerSize.height * 16) / 9;

  const letterboxIsHorizontal = heightForWidth > containerSize.height;

  return letterboxIsHorizontal
    ? {
        width: '100%',
        height: heightForWidth,
        overflowY: 'clip',
        top: -(heightForWidth - containerSize.height) / 2,
        left: 0,
        scale,
      }
    : {
        height: '100%',
        width: widthForHeight,
        overflowX: 'clip',
        left: -(widthForHeight - containerSize.width) / 2,
        top: 0,
        scale,
      };
};
